/**
=========================================================
* Dashboard Summary Page
=========================================================
* Shows high-level stats using the /dashboard API
*/

import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { dashboardAPI } from "services/api";

function StatCard({ label, value }) {
  return (
    <Card>
      <CardContent>
        <SoftTypography variant="button" color="text" fontWeight="medium">
          {label}
        </SoftTypography>
        <SoftTypography variant="h4" fontWeight="bold" mt={1}>
          {value ?? "-"}
        </SoftTypography>
      </CardContent>
    </Card>
  );
}

function DashboardSummary() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await dashboardAPI.getSummary();
        const resData = response.data;

        if (resData.status === 1) {
          setData(resData);
        } else {
          setError(resData.message || "Failed to load dashboard summary.");
        }
      } catch (err) {
        console.error("Error loading dashboard summary:", err);
        setError("Failed to load dashboard summary. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, []);

  const title = "Dashboard Summary";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Helmet>
          <title>{title}</title>
        </Helmet>

        <SoftBox mb={3}>
          <SoftTypography variant="h3" fontWeight="bold">
            {title}
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            High-level overview of auctions, leagues, groups and users.
          </SoftTypography>
        </SoftBox>

        {loading ? (
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={6}
          >
            <CircularProgress />
          </SoftBox>
        ) : error ? (
          <SoftBox py={2}>
            <Card>
              <CardContent>
                <SoftTypography variant="button" color="error" fontWeight="medium">
                  {error}
                </SoftTypography>
              </CardContent>
            </Card>
          </SoftBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Completed Auctions"
                value={data?.completed_auctions_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Upcoming Active Leagues"
                value={data?.active_future_leagues_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Active Groups"
                value={data?.active_groups_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Inactive Groups"
                value={data?.inactive_groups_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Public Registration Open"
                value={data?.public_registration_open_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Total Users"
                value={data?.total_users_count}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                label="Verified Users"
                value={data?.total_verified_users_count}
              />
            </Grid>
          </Grid>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardSummary;

